// Finance reports and consolidation (06 §6.5–6.6). Pure: the server hands in account totals read from
// the ledger and gets structured figures back. Money is integer minor units; rates are decimal text
// and every conversion is exact rational maths (never a float, never 1:1).
import { getAccount } from './accounting';
import { convert, isCurrencyCode, money, type CurrencyCode } from './money';

export interface AccountTotal {
  readonly accountCode: string;
  readonly debitMinor: number;
  readonly creditMinor: number;
}

interface Pair {
  debit: number;
  credit: number;
}

/** Sums rows per account. */
export function totalsByAccount(rows: Iterable<AccountTotal>): Map<string, Pair> {
  const out = new Map<string, Pair>();
  for (const r of rows) {
    const have = out.get(r.accountCode) ?? { debit: 0, credit: 0 };
    have.debit += r.debitMinor;
    have.credit += r.creditMinor;
    out.set(r.accountCode, have);
  }
  return out;
}

const nameOf = (code: string): string => getAccount(code)?.name ?? `Account ${code}`;
const typeOf = (code: string): string => getAccount(code)?.type ?? 'other';

// ------------------------------------------------------------ trial balance

export interface TrialBalanceRow {
  readonly accountCode: string;
  readonly name: string;
  readonly type: string;
  readonly debitMinor: number;
  readonly creditMinor: number;
}

export interface TrialBalance {
  readonly rows: TrialBalanceRow[];
  readonly totalDebitMinor: number;
  readonly totalCreditMinor: number;
  readonly balanced: boolean;
}

/** Each account's net balance in the debit or the credit column; the two columns must be equal. */
export function trialBalance(totals: ReadonlyMap<string, Pair>): TrialBalance {
  const rows: TrialBalanceRow[] = [...totals]
    .map(([accountCode, t]) => {
      const net = t.debit - t.credit;
      return {
        accountCode,
        name: nameOf(accountCode),
        type: typeOf(accountCode),
        debitMinor: net > 0 ? net : 0,
        creditMinor: net < 0 ? -net : 0,
      };
    })
    .filter((r) => r.debitMinor !== 0 || r.creditMinor !== 0)
    .sort((a, b) => a.accountCode.localeCompare(b.accountCode));
  const totalDebitMinor = rows.reduce((s, r) => s + r.debitMinor, 0);
  const totalCreditMinor = rows.reduce((s, r) => s + r.creditMinor, 0);
  return {
    rows,
    totalDebitMinor,
    totalCreditMinor,
    balanced: totalDebitMinor === totalCreditMinor,
  };
}

// ------------------------------------------------------------------- P&L

export interface StatementLine {
  readonly accountCode: string;
  readonly name: string;
  readonly amountMinor: number;
}

export interface Pnl {
  readonly revenue: StatementLine[];
  readonly costOfSales: StatementLine[];
  readonly operatingCosts: StatementLine[];
  readonly other: StatementLine[];
  readonly revenueMinor: number;
  readonly costOfSalesMinor: number;
  readonly grossMarginMinor: number;
  readonly operatingCostsMinor: number;
  readonly otherMinor: number;
  readonly netResultMinor: number;
}

export type PnlSection = 'revenue' | 'costOfSales' | 'operatingCosts' | 'other';

/** Which part of the P&L an account belongs to, or undefined for a balance-sheet account. */
export function pnlSectionOf(accountCode: string): PnlSection | undefined {
  switch (typeOf(accountCode)) {
    case 'revenue':
    case 'contra_revenue':
      return 'revenue';
    case 'cogs':
      return 'costOfSales';
    case 'opex':
      return 'operatingCosts';
    default:
      return getAccount(accountCode)?.type === 'other' ? 'other' : undefined;
  }
}

/**
 * Amount of a P&L account for the statement: revenue lines are credit − debit (discounts, a contra,
 * come out negative); every cost line is debit − credit.
 */
export function pnlAmount(accountCode: string, t: Pair): number {
  return pnlSectionOf(accountCode) === 'revenue' ? t.credit - t.debit : t.debit - t.credit;
}

const sumLines = (lines: readonly StatementLine[]): number =>
  lines.reduce((s, l) => s + l.amountMinor, 0);

/** The movement of a period as a profit and loss statement. */
export function profitAndLoss(movement: ReadonlyMap<string, Pair>): Pnl {
  const sections: Record<PnlSection, StatementLine[]> = {
    revenue: [],
    costOfSales: [],
    operatingCosts: [],
    other: [],
  };
  for (const [accountCode, t] of movement) {
    const section = pnlSectionOf(accountCode);
    if (!section) continue;
    const amountMinor = pnlAmount(accountCode, t);
    if (amountMinor === 0) continue;
    sections[section].push({ accountCode, name: nameOf(accountCode), amountMinor });
  }
  for (const lines of Object.values(sections)) {
    lines.sort((a, b) => a.accountCode.localeCompare(b.accountCode));
  }
  const revenueMinor = sumLines(sections.revenue);
  const costOfSalesMinor = sumLines(sections.costOfSales);
  const operatingCostsMinor = sumLines(sections.operatingCosts);
  const otherMinor = sumLines(sections.other);
  return {
    ...sections,
    revenueMinor,
    costOfSalesMinor,
    grossMarginMinor: revenueMinor - costOfSalesMinor,
    operatingCostsMinor,
    otherMinor,
    netResultMinor: revenueMinor - costOfSalesMinor - operatingCostsMinor - otherMinor,
  };
}

// ----------------------------------------------------------- balance sheet

export interface BalanceSheet {
  readonly assets: StatementLine[];
  readonly liabilities: StatementLine[];
  readonly equity: StatementLine[];
  readonly currentResultMinor: number;
  readonly totalAssetsMinor: number;
  readonly totalLiabilitiesMinor: number;
  readonly totalEquityMinor: number;
  readonly balanced: boolean;
}

/** Which side of the balance sheet an account is on, or undefined for a P&L account. */
export function bsSectionOf(accountCode: string): 'assets' | 'liabilities' | 'equity' | undefined {
  switch (typeOf(accountCode)) {
    case 'asset':
    case 'contra_asset':
      return 'assets';
    case 'liability':
      return 'liabilities';
    case 'equity':
      return 'equity';
    default:
      return undefined;
  }
}

/** Assets are debit − credit (accumulated depreciation comes out negative); the rest credit − debit. */
export function bsAmount(accountCode: string, t: Pair): number {
  return bsSectionOf(accountCode) === 'assets' ? t.debit - t.credit : t.credit - t.debit;
}

/**
 * The position at the end of a period, from the cumulative totals. Until a year-end close moves the
 * result to 3200, the profit or loss since the books began is shown as the current result, so that
 * assets equal liabilities plus equity.
 */
export function balanceSheet(cumulative: ReadonlyMap<string, Pair>): BalanceSheet {
  const sections = { assets: [], liabilities: [], equity: [] } as Record<
    'assets' | 'liabilities' | 'equity',
    StatementLine[]
  >;
  for (const [accountCode, t] of cumulative) {
    const section = bsSectionOf(accountCode);
    if (!section) continue;
    const amountMinor = bsAmount(accountCode, t);
    if (amountMinor === 0) continue;
    sections[section].push({ accountCode, name: nameOf(accountCode), amountMinor });
  }
  for (const lines of Object.values(sections)) {
    lines.sort((a, b) => a.accountCode.localeCompare(b.accountCode));
  }
  const currentResultMinor = profitAndLoss(cumulative).netResultMinor;
  const totalAssetsMinor = sumLines(sections.assets);
  const totalLiabilitiesMinor = sumLines(sections.liabilities);
  const totalEquityMinor = sumLines(sections.equity) + currentResultMinor;
  return {
    ...sections,
    currentResultMinor,
    totalAssetsMinor,
    totalLiabilitiesMinor,
    totalEquityMinor,
    balanced: totalAssetsMinor === totalLiabilitiesMinor + totalEquityMinor,
  };
}

// --------------------------------------------------------------- VAT return

export interface VatFigures {
  readonly standardRatedSalesMinor: number;
  readonly outputVatMinor: number;
  readonly inputVatMinor: number;
  readonly netVatPayableMinor: number;
}

/**
 * The net sales the month's output VAT is on: the revenue lines (credit − debit, discounts and credit
 * notes reduce it) of the journals that carry output VAT (2030). Revenue with no VAT on it (other
 * income, points and vouchers given away) is not a standard-rated supply, so the P&L revenue is not
 * the VAT base: on staging it made 15% VAT read as 15.67%.
 */
export function vatSalesBaseMinor(
  journals: readonly {
    readonly lines: readonly {
      readonly accountCode: string;
      readonly debitMinor: number;
      readonly creditMinor: number;
    }[];
  }[],
): number {
  let base = 0;
  for (const j of journals) {
    if (!j.lines.some((l) => l.accountCode === '2030')) continue;
    for (const l of j.lines) {
      if (pnlSectionOf(l.accountCode) === 'revenue') base += l.creditMinor - l.debitMinor;
    }
  }
  return base;
}

/**
 * Output VAT (2030, credit − debit: credit notes reduce it) against input VAT (1040, debit − credit)
 * for the month's movement, with the net sales the output VAT is on (06 §6.6): `salesBaseMinor`
 * from `vatSalesBaseMinor`, or the P&L revenue when the journals were not read.
 */
export function vatFigures(
  movement: ReadonlyMap<string, Pair>,
  salesBaseMinor?: number,
): VatFigures {
  const out = movement.get('2030') ?? { debit: 0, credit: 0 };
  const input = movement.get('1040') ?? { debit: 0, credit: 0 };
  const outputVatMinor = out.credit - out.debit;
  const inputVatMinor = input.debit - input.credit;
  return {
    standardRatedSalesMinor: salesBaseMinor ?? profitAndLoss(movement).revenueMinor,
    outputVatMinor,
    inputVatMinor,
    netVatPayableMinor: outputVatMinor - inputVatMinor,
  };
}

export interface VatInputRow {
  readonly journalId: string;
  readonly journalNo: number | null;
  readonly date: string;
  readonly invoiceNo: string;
  readonly supplierVatNo: string;
  readonly netMinor: number;
  readonly vatMinor: number;
}

/** The supplier lines of the VAT return: every 1040 line a purchase posted, with what it was on. */
export function vatInputRows(
  journals: readonly {
    readonly id: string;
    readonly journalNo?: number | null | undefined;
    readonly businessDate: string;
    readonly lines: readonly {
      readonly accountCode: string;
      readonly debitMinor: number;
      readonly creditMinor: number;
      readonly dimensions?: Readonly<Record<string, string>> | undefined;
    }[];
  }[],
): VatInputRow[] {
  const rows: VatInputRow[] = [];
  for (const j of journals) {
    for (const l of j.lines) {
      if (l.accountCode !== '1040') continue;
      const d = l.dimensions ?? {};
      rows.push({
        journalId: j.id,
        journalNo: j.journalNo ?? null,
        date: j.businessDate,
        invoiceNo: d['invoiceNo'] ?? '',
        supplierVatNo: d['supplierVatNo'] ?? '',
        netMinor: Number(d['vatNetMinor'] ?? 0),
        vatMinor: l.debitMinor - l.creditMinor,
      });
    }
  }
  return rows.sort(
    (a, b) => a.date.localeCompare(b.date) || a.journalId.localeCompare(b.journalId),
  );
}

// -------------------------------------------------------------------- rates

const SCALE = 12;
const ONE = 10n ** BigInt(SCALE);

/** Decimal text → an integer scaled by 10^12, or undefined when it is not a positive decimal. */
function scaled(rate: string): bigint | undefined {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(rate.trim());
  if (!m) return undefined;
  const fraction = (m[2] ?? '').slice(0, SCALE).padEnd(SCALE, '0');
  const value = BigInt((m[1] as string) + fraction);
  return value > 0n ? value : undefined;
}

function unscale(value: bigint): string {
  const whole = value / ONE;
  const fraction = (value % ONE).toString().padStart(SCALE, '0').replace(/0+$/, '');
  return fraction === '' ? `${whole}` : `${whole}.${fraction}`;
}

/** The rate the other way round, to 12 decimals. */
export function invertRate(rate: string): string | undefined {
  const value = scaled(rate);
  if (value === undefined) return undefined;
  return unscale((ONE * ONE + value / 2n) / value);
}

/** The arithmetic mean of dated rates, to 12 decimals, rounded half-up. Undefined for no usable rate. */
export function averageRate(rates: readonly string[]): string | undefined {
  const values = rates.map(scaled).filter((v): v is bigint => v !== undefined);
  if (values.length === 0) return undefined;
  const sum = values.reduce((a, b) => a + b, 0n);
  const n = BigInt(values.length);
  return unscale((2n * sum + n) / (2n * n));
}

/** Translates an amount with a rate (1 `from` buys `rate` `to`). Same currency needs none; no rate throws. */
export function translateMinor(
  amountMinor: number,
  from: string,
  to: string,
  rate: string | null,
  date: string,
): number {
  if (from === to) return amountMinor;
  if (rate === null || !isCurrencyCode(from) || !isCurrencyCode(to)) {
    throw new Error(`No usable rate from ${from} to ${to}`);
  }
  return convert(money(amountMinor, from as CurrencyCode), {
    from: from as CurrencyCode,
    to: to as CurrencyCode,
    rate,
    date,
  }).amountMinor;
}

// ------------------------------------------------------------ consolidation

export interface EntityBooks {
  readonly entityId: string;
  readonly currency: string;
  /** The period's movement per account. */
  readonly movement: ReadonlyMap<string, Pair>;
  /** Cumulative totals to the end of the period. */
  readonly cumulative: ReadonlyMap<string, Pair>;
  /** To the reporting currency. Null only when the entity is already in it. */
  readonly averageRate: string | null;
  readonly closingRate: string | null;
  /** Net balances of 1200 (receivable) and 2100 (payable) by the other entity, in this entity's currency. */
  readonly intercompany: {
    readonly counterEntityId: string;
    readonly receivableMinor: number;
    readonly payableMinor: number;
  }[];
}

export interface TranslatedLine {
  readonly accountCode: string;
  readonly name: string;
  readonly byEntity: Record<string, number>;
  readonly amountMinor: number;
}

export interface Elimination {
  readonly fromEntityId: string;
  readonly toEntityId: string;
  readonly receivableMinor: number;
  readonly payableMinor: number;
  readonly eliminatedMinor: number;
  readonly differenceMinor: number;
}

export interface Consolidation {
  readonly pnl: {
    readonly revenue: TranslatedLine[];
    readonly costOfSales: TranslatedLine[];
    readonly operatingCosts: TranslatedLine[];
    readonly other: TranslatedLine[];
    readonly revenueMinor: number;
    readonly costOfSalesMinor: number;
    readonly grossMarginMinor: number;
    readonly operatingCostsMinor: number;
    readonly otherMinor: number;
    readonly netResultMinor: number;
  };
  readonly balanceSheet: {
    readonly assets: TranslatedLine[];
    readonly liabilities: TranslatedLine[];
    readonly equity: TranslatedLine[];
    readonly priorResultMinor: number;
    readonly currentResultMinor: number;
    readonly translationDifferenceMinor: number;
    readonly totalAssetsMinor: number;
    readonly totalLiabilitiesMinor: number;
    readonly totalEquityMinor: number;
    readonly balanced: boolean;
  };
  readonly eliminations: Elimination[];
  readonly entityResults: { entityId: string; localMinor: number; groupMinor: number }[];
}

type Lines = Map<string, { byEntity: Record<string, number>; amountMinor: number }>;

function addLine(
  lines: Lines,
  code: string,
  entityId: string | undefined,
  local: number,
  group: number,
): void {
  const have = lines.get(code) ?? { byEntity: {}, amountMinor: 0 };
  if (entityId !== undefined) have.byEntity[entityId] = (have.byEntity[entityId] ?? 0) + local;
  have.amountMinor += group;
  lines.set(code, have);
}

const toLines = (lines: Lines): TranslatedLine[] =>
  [...lines]
    .map(([accountCode, v]) => ({ accountCode, name: nameOf(accountCode), ...v }))
    .filter((l) => l.amountMinor !== 0 || Object.values(l.byEntity).some((n) => n !== 0))
    .sort((a, b) => a.accountCode.localeCompare(b.accountCode));

const sumTranslated = (lines: readonly TranslatedLine[]): number =>
  lines.reduce((s, l) => s + l.amountMinor, 0);

/**
 * Group figures in the reporting currency (06 §6.5). The P&L of the month is translated account by
 * account at the month's average rate; the balance sheet at the closing rate. The result of the
 * months before is carried at the closing rate (there are no historical rates to do better), so the
 * translation difference that reaches 3300 is this month's result at the closing rate less the same
 * result at the average rate, plus rounding. Intercompany receivables (1200) and payables (2100) are
 * matched pair by pair and eliminated; what does not cancel stays on the balance sheet and is listed.
 */
export function consolidate(
  entities: readonly EntityBooks[],
  reportingCurrency: string,
  rateDate: string,
): Consolidation {
  const pnl = {
    revenue: new Map() as Lines,
    costOfSales: new Map() as Lines,
    operatingCosts: new Map() as Lines,
    other: new Map() as Lines,
  };
  const bs = {
    assets: new Map() as Lines,
    liabilities: new Map() as Lines,
    equity: new Map() as Lines,
  };
  let priorResultMinor = 0;
  const entityResults: Consolidation['entityResults'] = [];
  const at = (e: EntityBooks, rate: string | null, minor: number): number =>
    translateMinor(minor, e.currency, reportingCurrency, rate, rateDate);

  for (const e of entities) {
    const movementPnl = profitAndLoss(e.movement);
    let groupResult = 0;
    for (const [code, t] of e.movement) {
      const section = pnlSectionOf(code);
      if (!section) continue;
      const local = pnlAmount(code, t);
      if (local === 0) continue;
      const group = at(e, e.averageRate, local);
      addLine(pnl[section], code, e.entityId, local, group);
      groupResult += section === 'revenue' ? group : -group;
    }
    entityResults.push({
      entityId: e.entityId,
      localMinor: movementPnl.netResultMinor,
      groupMinor: groupResult,
    });
    for (const [code, t] of e.cumulative) {
      const section = bsSectionOf(code);
      if (!section) continue;
      const local = bsAmount(code, t);
      if (local === 0) continue;
      addLine(bs[section], code, e.entityId, local, at(e, e.closingRate, local));
    }
    const cumulativeResult = profitAndLoss(e.cumulative).netResultMinor;
    priorResultMinor += at(e, e.closingRate, cumulativeResult - movementPnl.netResultMinor);
  }

  // Intercompany: X's payable to Y (2100) against Y's receivable from X (1200), at closing rates.
  const eliminations: Elimination[] = [];
  const payable = new Map<string, number>();
  const receivable = new Map<string, number>();
  for (const e of entities) {
    for (const ic of e.intercompany) {
      if (ic.payableMinor !== 0) {
        payable.set(`${e.entityId}>${ic.counterEntityId}`, at(e, e.closingRate, ic.payableMinor));
      }
      if (ic.receivableMinor !== 0) {
        receivable.set(
          `${ic.counterEntityId}>${e.entityId}`,
          at(e, e.closingRate, ic.receivableMinor),
        );
      }
    }
  }
  for (const key of [...new Set([...payable.keys(), ...receivable.keys()])].sort()) {
    const [from, to] = key.split('>') as [string, string];
    const owes = payable.get(key) ?? 0;
    const owed = receivable.get(key) ?? 0;
    const eliminatedMinor = Math.min(Math.max(owes, 0), Math.max(owed, 0));
    eliminations.push({
      fromEntityId: from,
      toEntityId: to,
      receivableMinor: owed,
      payableMinor: owes,
      eliminatedMinor,
      differenceMinor: owed - owes,
    });
    if (eliminatedMinor > 0) {
      addLine(bs.assets, '1200', undefined, 0, -eliminatedMinor);
      addLine(bs.liabilities, '2100', undefined, 0, -eliminatedMinor);
    }
  }

  const revenue = toLines(pnl.revenue);
  const costOfSales = toLines(pnl.costOfSales);
  const operatingCosts = toLines(pnl.operatingCosts);
  const other = toLines(pnl.other);
  const revenueMinor = sumTranslated(revenue);
  const costOfSalesMinor = sumTranslated(costOfSales);
  const operatingCostsMinor = sumTranslated(operatingCosts);
  const otherMinor = sumTranslated(other);
  const currentResultMinor = revenueMinor - costOfSalesMinor - operatingCostsMinor - otherMinor;

  const assets = toLines(bs.assets);
  const liabilities = toLines(bs.liabilities);
  const equity = toLines(bs.equity);
  const totalAssetsMinor = sumTranslated(assets);
  const totalLiabilitiesMinor = sumTranslated(liabilities);
  const equityBefore = sumTranslated(equity);
  // 3300 is what makes the group balance sheet balance: it carries whatever the rates did.
  const translationDifferenceMinor =
    totalAssetsMinor - totalLiabilitiesMinor - equityBefore - priorResultMinor - currentResultMinor;
  const cta = equity.find((l) => l.accountCode === '3300');
  const equityLines = cta
    ? equity.map((l) =>
        l.accountCode === '3300'
          ? { ...l, amountMinor: l.amountMinor + translationDifferenceMinor }
          : l,
      )
    : [
        ...equity,
        {
          accountCode: '3300',
          name: nameOf('3300'),
          byEntity: {},
          amountMinor: translationDifferenceMinor,
        },
      ].sort((a, b) => a.accountCode.localeCompare(b.accountCode));
  const totalEquityMinor = sumTranslated(equityLines) + priorResultMinor + currentResultMinor;

  return {
    pnl: {
      revenue,
      costOfSales,
      operatingCosts,
      other,
      revenueMinor,
      costOfSalesMinor,
      grossMarginMinor: revenueMinor - costOfSalesMinor,
      operatingCostsMinor,
      otherMinor,
      netResultMinor: currentResultMinor,
    },
    balanceSheet: {
      assets,
      liabilities,
      equity: equityLines,
      priorResultMinor,
      currentResultMinor,
      translationDifferenceMinor,
      totalAssetsMinor,
      totalLiabilitiesMinor,
      totalEquityMinor,
      balanced: totalAssetsMinor === totalLiabilitiesMinor + totalEquityMinor,
    },
    eliminations,
    entityResults,
  };
}
